import React, { useContext, useEffect, useLayoutEffect } from 'react';
import { graphql } from 'gatsby';
import { css, Global } from '@emotion/react';
import Image from 'gatsby-plugin-sanity-image';
import TicketWidget from '../components/ticket-widget';
import { formatProgramTimes, formatDates } from '../utility';
import { ThemeContext } from '../components/theme-provider';
import SEO from '../components/seo';
import FullImage from '../components/full-image';
import Information from '../components/information';
import PortableText from '../components/portable-text';
import ProgramRow from '../components/program-row';
import Button from '../components/button';
import { hasContent } from '../content';

export default function Program({ data }) {
  const theme = useContext(ThemeContext);
  const times = formatProgramTimes(data.program);
  const ticketWidgetCode = data.program.ticketWidgetCode?.trim();

  useEffect(() => {
    theme.setGalleryImages([data.program?.featureImage].concat(data.program?.images));
  }, []);

  useLayoutEffect(() => {
    if (data.program.related.length > 0) {
      theme.setGrayFooter(false);
    } else {
      theme.setGrayFooter(true);
    }
  }, []);

  return (
    <div
      className="type--45"
      css={css`
        background-color: var(--white);
      `}
    >
      <SEO
        title={`${data.program.title} — ${formatDates(data.program.startDate, data.program.endDate)}`}
        description={(data.program._rawContent) ? data.program._rawContent.map(el => el.children).flat().map((el) => el?.text).join('') : null}
        imageID={(data.program.featureImage) ? data.program.featureImage.asset._id : null}
      />
      <div
        css={css`
          background-color: var(--primary);
        `}
      >
        {!data.program?.featureVideo && <FullImage
          text={`
            ${(data.program.locationTitle) ? `${data.program.locationTitle}/\u200b` : ''}
            ${(data.program.people.length > 0) ? `${data.program.people.map(person => person.name).join(', ')}/\u200b` : ''}
            ${data.program.title}/
          `}
          includeTitle={!data.program.locationTitle}
          image={data.program?.featureImage}
        />}
        {data.program?.featureVideo && <FullImage
          text={`
            ${(data.program.locationTitle) ? `${data.program.locationTitle}/\u200b` : ''}
            ${(data.program.people.length > 0) ? `${data.program.people.map(person => person.name).join(', ')}/\u200b` : ''}
            ${data.program.title}/
          `}
          includeTitle={!data.program.locationTitle}
          video={data.program?.featureVideo}
        />}
      </div>
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
          padding: 0 var(--margin);
          margin-bottom: 15rem;

          > div {
            grid-column: span 4;
            white-space: pre-line;

            @media (max-width: 1080px) {
              grid-column: span 6;
            }

            @media (max-width: 450px) {
              grid-column: span 12;
            }
          }
        `}
      >
        <div>
          <Information
            title="Location"
            subtitle={data.program.location}
          >{(data.program.address) ? data.program.address : 'The Substation,\n1 Market Street,\nNewport, Victoria\n3015, Australia'}</Information>
          <Information
            title="Dates"
          >{formatDates(data.program.startDate, data.program.endDate)}</Information>
        </div>
        <div>
          {times.length > 0 && <Information
            title="Times"
          >
            {times
              .map((time, i) => (
                <div key={i}>
                  {time}
                </div>
              ))
            }
          </Information>}
          <Information
            title="Format"
          >
            {(data.program.format || []).map(format => format.title).filter(hasContent).join('/\u200B')}
          </Information>
          {(hasContent(data.program.accessibility?._rawContent) || hasContent(data.program._rawAccessibilityText) || data.program.accessibilityList?.some(item => hasContent(item._rawContent))) && <Information
            title="Accessibility"
          >
            {hasContent(data.program._rawAccessibilityText) && <PortableText
              css={css`
                margin-bottom: 2rem;

                p {
                  margin-bottom: 2rem;
                }
              `}
              blocks={data.program._rawAccessibilityText}
            />}
            {data.program.accessibilityList?.length > 0 && data.program.accessibilityList
              .map((item, i) => (
                <PortableText
                  css={css`
                    margin-bottom: 2rem;

                    p {
                      margin-bottom: 2rem;
                    }
                  `}
                  blocks={item?._rawContent}
                />
              ))
            }
            {(!data.program.accessibilityList?.some(item => hasContent(item._rawContent)) && !hasContent(data.program._rawAccessibilityText)) && <PortableText
              blocks={data.program.accessibility?._rawContent}
            />}
          </Information>}
        </div>
        <div>
          {hasContent(data.program._rawAdmission) && <Information
            title="Admission"
          >
            <PortableText
              blocks={data.program._rawAdmission}
            />
          </Information>}
          {ticketWidgetCode && <Information
            title="Tickets"
          ><a href="#tickets">Book now</a></Information>}
          {(!ticketWidgetCode && hasContent(data.program._rawTickets)) && <Information
            title="Tickets"
          >
            <PortableText
              blocks={data.program._rawTickets}
            />
          </Information>}
        </div>
      </div>
      <div
        css={css`
          padding: 0 var(--margin);
          margin-bottom: 15rem;
        `}
      >
        <PortableText
          blocks={data.program._rawContent}
        />
      </div>
      {ticketWidgetCode && <div
        css={css`
          padding: 0 var(--margin);
          margin-bottom: 15rem;
        `}
      >
        <h2
          className="type--70"
          id="tickets"
          css={css`
            margin-bottom: 5rem;
          `}
        >Tickets/</h2>
        <div
          className="type--20"
          css={css`
            #ft_ed_intro {
              border: 0;
              color: var(--black);
              background-color: transparent;
              margin-bottom: 20px;
            }

            .ft_ed_headerRow > div {
              font-size: 2rem;
              line-height: 2;
            }

            [id^="ft_eventDates"] {
              max-height: none;
            }

            [class^="ft_"] {
              font-size: 2rem;
              line-height: 2;
              background-color: transparent;
              border-color: var(--black);
            }

            input[type="radio"] {
              accent-color: var(--black);
            }

            .ft_ed_headerRow {
              display: flex;
              height: auto;
            }

            .ft_ed_headerRow .ft_ed_buy,
            .ft_ed_headerRow .ft_ed_status {
              display: none;
            }

            .ft_ed_headerRow .ft_ed_dateTime {
              flex: 1;
              float: none;
              position: static;
              width: auto;
              white-space: nowrap;
              border-bottom: 1px solid var(--black);
            }

            .ft_ed_timeSlotRow {
              display: flex;
            }

            .ft_ed_timeSlotRow .ft_ed_buy {
              flex: 0 0 48px;
            }

            .ft_ed_timeSlotRow .ft_ed_dateTimeSlot {
              flex: 1;
              min-width: 0;
              width: auto;
            }

            [id^="ft_ed_cal"] .ft_ed_cal_monthSwitch a {
              color: var(--black) !important;
            }

            .ft_cancelled, .ft_postponed, .ft_standby, .ft_soldout {
              filter: none;
              color: var(--green);
            }

            [name="ft_ed_timeRow"] {
              padding: 5px 0;
            }

            select.form-control {
              padding: 2px;
            }

            .fticon-status-soldout,
            .fticon-status-sellingfast {
              position: relative;
              top: -3px;
              left: -3px;
            }

            .ft_pricing input[type=text], .ft_pricing input[type=email], .ft_pricing textarea {
              border: 1px solid var(--black);
            }

            .ft_pricing input[type=text] {
              height: 38px;
              margin-left: 30px;
              margin-right: 10px;
            }

            [type="checkbox"], [type="radio"] {
              margin-right: 10px;
            }

            .ft_ep_priceRow .ft_ep_conditionsRow {
              width: calc(100% - 120px);
            }

            .ft_ep_priceRow .ft_ep_conditionsRow {
              color: var(--black);
            }

            .ft_ep_headerRow {
              height: auto;
              border-bottom: 1px solid var(--black);
            }

            #ft_pricing > .ft_ep_headerRow {
              border: 0;
            }

            .ft_ep_qty {
              width: 100px;
              padding-left: 0;
            }

            .ft_ep_type {
              width: calc(100% - 100px - 70px);
            }

            .ft_ep_priceRow .ft_ep_conditionsRow {
              padding-top: 10px;
              margin-left: 100px;
            }

            .ft_radio input[type=radio] + label, .ft_radio input[type=checkbox] + label {
              color: var(--black);
              background: none;
              border: 1px solid var(--black);
              margin-bottom: 5px;
              margin-right: 5px;
              box-shadow: none !important;
              text-shadow: none !important;
            }

            .ft_radio input[type=radio]:checked + label, .ft_radio input[type=checkbox]:checked + label {
              color: var(--white);
              background-color: var(--black);
            }

            .ft_radio input[type=radio]:hover + label, .ft_radio input[type=checkbox]:hover + label {
              color: var(--white);
              background-color: var(--black);
            }

            button[class^="ft_"] {
              color: var(--white);
              background-color: var(--black);
              cursor: pointer;
            }

            button[class^="ft_"]:hover {
              color: var(--black);
              background-color: var(--green);
            }

            #ft_pricingAdditionalMessage {
              color: var(--black);
            }

            & *, .ft_notification_notification {
              color: var(--black);
              font-weight: normal;
              border-color: var(--black);
              background-color: transparent;
            }

            & div {
              background-color: transparent;
            }
          `}
        >

          <Global
            styles={css`
              @keyframes move {
                0% {
                  background-position: 0px 0px;
                }
                10% {
                  background-position: 1000px 400px;
                }
                20% {
                  background-position: 300px 300px;
                }
                30% {
                  background-position: 1200px 100px;
                }
                40% {
                  background-position: 600px 1000px;
                }
                50% {
                  background-position: 800px 50px;
                }
                60% {
                  background-position: 700px 800px;
                }
                70% {
                  background-position: 1400px 400px;
                }
                80% {
                  background-position: 200px 900px;
                }
                90% {
                  background-position: 1100px 500px;
                }
                100% {
                  background-position: 400px 850px;
                }
              }

              #ft_notificationIcon {
                filter: brightness(0%);
              }

              #ft_notificationBG, #ft_fcBG {
                background-color: var(--green) !important;
                will-change: background-position;
                animation: move 625ms infinite;
              }

              .ft_fade.ft_itemVisible80, .ft_fade.ft_itemVisible40 {
                opacity: 1 !important;
              }
              
              .ft_notification_notification {
                border: 1px solid var(--black);
                border-radius: 0 !important;
              }

              .ft_notification_notification, #ft_notificationMessage {
                font-family: 'Monument', sans-serif !important;
                color: var(--black) !important;
                font-weight: normal !important;
                background-color: transparent;
              }

             #ft_ferveCart, #ft_ferveCartView, #ft_ferveCart * {
                font-family: 'Monument', sans-serif !important;
                font-size: 2rem !important;
                font-weight: normal !important;
                line-height: 1.1 !important;
                color: var(--black) !important;
                border-radius: 0 !important;
                border: 0 !important;

                @media (max-width: 700px) {
                  font-size: 2.4rem !important;
                  line-height: 1.15 !important;
                }
              }

              #ft_ferveCart {
                padding: 0;
                box-sizing: content-box;
                border: 15px solid var(--white) !important;
              }

              #ft_fcHeader2 {
                height: 35px !important;
                text-transform: uppercase;
                background-color: transparent !important;
                border-bottom: 1px solid var(--black) !important;
                margin-top: 10px;
                margin-bottom: 10px;
              }

              #ft_fcHeader2 .ft_fcItemTitle {
                left: 0 !important;
              }

              .ft_fcItemTotal {
                right: 0 !important;
              }

              #ft_fcLogo {
                display: none !important;
              }

              #ft_fcButtons {
                position: static !important;
                padding-top: 20px !important;
                border-top: 1px solid var(--black) !important;
                margin-bottom: 40px;
              }

              #ft_fcButtons button {
                background-color: transparent !important;
                border: 1px solid var(--black) !important;
                margin-left: 0 !important;
                margin-right: 10px;
                cursor: pointer;
              }

              #ft_fcButtons button:hover {
                color: var(--white) !important;
                background-color: var(--black) !important;
              }

              .ft_timeout {
                position: static !important;
              }

              #ft_ed_cal {
                border: 1px solid var(--black);
                margin-bottom: 20px;

                .ft_ed_cal_days {
                  border-bottom: 1px solid var(--black);
                }

                .ft_ed_cal_column_selected {
                  background-color: var(--primary);
                }

                .ft_ed_cal_day, .ft_ed_cal_column {
                  line-height: 1.7;
                  border: 0;

                  a {
                    background-color: var(--primary);
                  }
                }

                .ft_ed_cal_columnInactive a {
                  background-color: transparent;
                }

                .ft_ed_cal_status {
                  background-color: var(--black);
                }

                .ft_ed_cal_row {
                  overflow: show;
                }

                .ft_ed_cal_column_today {
                  color: var(--primary);
                }

              }

              div[id^="ft_eventCalendar_"], div[id^="ft_eventDates_"] {
                float: left;
              }

              div[id^="ft_eventCalendar_"] {
                margin-bottom: 3rem;
              }

              div[id^="ft_eventPricing_"] {
                clear: both;
              }

              .ft_eventDate {
                text-decoration: underline;
              }

              div[id^="ft_eventCalendar_"] + div[id^="ft_eventDates_"] {
                position: relative;
                top: -10px;
                margin-left: 50px;
                margin-bottom: 20px;
              }

              .ft_ed_cal_key {
                display: none;
                border-bottom: 1px solid var(--black);
              }

            `}
          />
          <TicketWidget key={ticketWidgetCode} html={ticketWidgetCode} tickets={data.program._rawTickets} />
        </div>
      </div>}
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
          padding: 0 var(--margin);
          padding-bottom: var(--bigMargin);
        `}
      >
        {data.program.images.length > 0 && data.program.images
          .map((image, i) => (
            <div
              key={i}
              css={css`
                position: relative;
                grid-column: span 4;
                padding-top: 100%;
                line-height: 0;
                cursor: pointer;

                @media (max-width: 1080px) {
                  grid-column: span 6;
                }

                @media (max-width: 700px) {
                  grid-column: span 12;
                  padding-top: 0;
                }

                img {
                  transition: filter 0.6s var(--curve);
                }

                &:hover {
                  img {
                    filter: blur(1.5rem);
                  }
                }
              `}
              onClick={() => {
                theme.setInitialGallerySlide(i + 1);
                theme.setShowGallery(true);
              }}
            >
              <div
                css={css`
                  position: absolute;
                  top: 0;
                  right: 0;
                  bottom: 0;
                  left: 0;

                  @media (max-width: 700px) {
                    position: static;
                  }
                `}
              >
                {image.asset && <Image
                  {...image}
                  width={1980}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                  loading="eager"
                />}
              </div>
            </div>
          ))
        }
      </div>
      {data.program._rawCredits && <div
        className="type--20"
        css={css`
          padding: 0 var(--margin);
          padding-bottom: var(--bigMargin);
        `}
      >
        <PortableText
          blocks={data.program._rawCredits}
        />
      </div>}
      {data.program.information.length > 0 && <div
        css={css`
          padding: 0 var(--margin);
          padding-bottom: 15rem;
        `}
      >
        <h2
          className="type--70"
          css={css`
            margin-bottom: 5rem;
          `}
        >Dive deeper/</h2>
        <div
          css={css`
            display: grid;
            grid-template-columns: repeat(12, 1fr);
            gap: var(--gutter);
          `}
        >
          {data.program.information.length > 0 && data.program.information
            .map((information, i) => (
              <div
                key={i}
                css={css`
                  grid-column: span 6;
                  margin-bottom: 3rem;

                  @media (max-width: 700px) {
                    grid-column: span 12;
                  }
                `}
              >
                {(information._type === 'richEmbed' && information.code) &&
                  <div>
                    <div
                      css={css`
                        position: relative;
                        padding-top: 56.25%;
                        margin-bottom: 1rem;

                        > * {
                          display: none;
                        }

                        iframe {
                          display: block;
                          position: absolute;
                          top: 0;
                          right: 0;
                          bottom: 0;
                          left: 0;
                          width: 100% !important;
                          height: 100% !important;
                        }
                      `}
                      dangerouslySetInnerHTML={{__html: information.code}}
                    ></div>
                    <div
                      className="type--20"
                      css={css`
                        &::before {
                          content: '';
                          display: inline-block;
                          width: 1.1rem;
                          height: 1.1rem;
                          border-radius: 50%;
                          background-color: var(--black);
                          margin-right: 1rem;
                        }
                      `}
                    >{information.type}</div>
                    <div
                      className="type--20"
                    >{information.title}</div>
                    <div
                      className="type--20"
                    >{information.subtitle}</div>
                  </div>
                }
                {information._type === 'richLink' &&
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={information.url}
                    css={css`
                      display: block;
                      position: relative;
                      line-height: 0;
                      padding-top: 56.25%;
                      border: 2px solid var(--black);

                      &:hover {
                        color: var(--white);
                        background-color: var(--black);

                        .has-bullet {
                          &::before {
                            background-color: var(--white);
                          }
                        }

                        .has-underline {
                          border-bottom-color: var(--white);
                        }
                      }
                    `}
                  >
                    <div
                      css={css`
                        position: absolute;
                        top: var(--gutter);
                        right: var(--gutter);
                        left: var(--gutter);
                      `}
                    >
                      <div
                        className="type--20 has-bullet"
                        css={css`
                          margin-bottom: 1rem;

                          &::before {
                            content: '';
                            display: inline-block;
                            width: 1.1rem;
                            height: 1.1rem;
                            border-radius: 50%;
                            background-color: var(--black);
                            margin-right: 1rem;
                          }
                        `}
                      >{information.type}</div>
                      <div
                        className="type--45"
                        css={css`
                          text-transform: uppercase;
                        `}
                      >{information.title}</div>
                      <div
                        className="type--45"
                      >{information.subtitle}</div>
                    </div>
                    <div
                      className="type--20"
                      css={css`
                        position: absolute;
                        right: var(--gutter);
                        bottom: var(--gutter);
                        left: var(--gutter);
                      `}
                    >
                      <span
                        className="has-underline"
                        css={css`
                          display: inline-block;
                          line-height: 1.1;
                          border-bottom: 2px solid var(--black);
                        `}
                      >
                        {information.linkTitle}
                      </span>
                    </div>
                  </a>
                }
                {information._type === 'pdf' &&
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={information.file.asset.url}
                    css={css`
                      display: block;
                      position: relative;
                      line-height: 0;
                      padding-top: 56.25%;
                      border: 2px solid var(--black);

                      &:hover {
                        color: var(--white);
                        background-color: var(--black);

                        .has-bullet {
                          &::before {
                            background-color: var(--white);
                          }
                        }

                        .has-underline {
                          border-bottom-color: var(--white);
                        }
                      }
                    `}
                  >
                    <div
                      css={css`
                        position: absolute;
                        top: var(--gutter);
                        right: var(--gutter);
                        left: var(--gutter);
                      `}
                    >
                      <div
                        className="type--20 has-bullet"
                        css={css`
                          margin-bottom: 1rem;

                          &::before {
                            content: '';
                            display: inline-block;
                            width: 1.1rem;
                            height: 1.1rem;
                            border-radius: 50%;
                            background-color: var(--black);
                            margin-right: 1rem;
                          }
                        `}
                      >Read</div>
                      <div
                        className="type--45"
                        css={css`
                          text-transform: uppercase;
                        `}
                      >{information.title}</div>
                      <div
                        className="type--45"
                      >{information.subtitle}</div>
                    </div>
                    <div
                      className="type--20"
                      css={css`
                        position: absolute;
                        right: var(--gutter);
                        bottom: var(--gutter);
                        left: var(--gutter);
                      `}
                    >
                      <span
                        className="has-underline"
                        css={css`
                          display: inline-block;
                          line-height: 1.1;
                          border-bottom: 2px solid var(--black);
                        `}
                      >
                        Download PDF
                      </span>
                    </div>
                  </a>
                }
              </div>
            ))
          }
        </div>
      </div>}
      {data.program.related.length > 0 && <div
        css={css`
          background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), var(--lightGray) 10rem, var(--lightGray) calc(100% - 10rem), rgba(255, 255, 255, 0));
          padding: 15rem var(--margin);
        `}
      >
        <h2
          className="type--70"
          css={css`
            margin-top: 5rem;
            margin-bottom: 5rem;
          `}
        >Related/</h2>
        {data.program.related.length > 0 && data.program.related
          .map((program, i) => (
            <ProgramRow program={program}/>
          ))
        }
        <div
          className="type--20"
          css={css`
            text-align: center;
          `}
        >
          <Button
            text="What's on"
            link="/whats-on"
            large
          />
        </div>
      </div>}
    </div>
  )
}

export const query = graphql`
  query ProgramTemplateQuery($slug: String!) {
    program: sanityProgram(slug: {current: {eq: $slug}}) {
      locationTitle
      people {
        name
        website
        slug {
          current
        }
      }
      title
      featureImage {
        ...Image
        asset {
          description
        }
      }
      featureVideo {
        asset {
          url
        }
      }
      location
      address
      startDate
      endDate
      timesText
      times {
        startDay
        endDay
        startTime
        endTime
      }
      format {
        title
      }
      accessibility {
        _rawContent(resolveReferences: {maxDepth: 10})
      }
      _rawAccessibilityText(resolveReferences: {maxDepth: 10})
      accessibilityList {
        _rawContent(resolveReferences: {maxDepth: 10})
      }
      _rawAdmission(resolveReferences: {maxDepth: 10})
      _rawTickets(resolveReferences: {maxDepth: 10})
      ticketWidgetCode
      _rawContent(resolveReferences: {maxDepth: 10})
      images {
        ...Image
        asset {
          description
        }
      }
      _rawCredits(resolveReferences: {maxDepth: 10})
      information {
        ... on SanityRichEmbed {
          _type
          type
          title
          subtitle
          code
        }
        ... on SanityRichLink {
          _type
          type
          title
          subtitle
          linkTitle
          url
        }
        ... on SanityPdf {
          _type
          title
          subtitle
          file {
            asset {
              url
            }
          }
        }
      }
      related {
        slug {
          current
        }
        format {
          title
        }
        featureImage {
          ...Image
        }
        people {
          name
          slug {
            current
          }
        }
        title
        startDate
        endDate
        _rawContent(resolveReferences: {maxDepth: 10})
      }
    }
  }
`
